import {notNull} from '../../../../vendor/plugins/clxMobileNetPortable/assets/responsive/js/common/assert';
import {ScriptDispatcher} from '../../../../vendor/plugins/clxMobileNetPortable/assets/responsive/js/common/scriptDispatcher';
import {ScrollToElement} from '../common/scrollToElement';
import {TriggerReloadEvent} from '../base/triggerReloadEvent';

export class ReloadManager {
    public cmsReloadModule(event: Event, filterOpenClose?: boolean, scroll?: boolean): void {
        event.preventDefault();
        event.stopImmediatePropagation();

        const target = event.target as HTMLInputElement;
        if (!target) return;

        const form = target.closest('form') as HTMLFormElement;
        if (!form) return;

        const elementID = target.id;

        this.reloadModule(form, filterOpenClose, scroll).finally(() => {
            const element = document.getElementById(elementID) as HTMLInputElement;
            if (element) {
                if (element.dataset.filteropen === '1') {
                    const datasheet = element.closest('.drop-datasheet') as HTMLElement;
                    if (datasheet) datasheet.classList.add('active');
                }

                const tagmanagerlayer = element.closest('.filterbox')?.getAttribute('data-tagmanagerlayer');
                if (tagmanagerlayer) {
                    try {
                        const contentForPush = JSON.parse(tagmanagerlayer);
                        const filterValue = this.getFilterValue(element);
                        const filterName = element.name;

                        contentForPush.ecommerce = null;
                        contentForPush.filter_name = filterName;
                        contentForPush.filter_value = filterValue;

                        // Push to dataLayer (Google Tag Manager)
                        (window as any).dataLayer.push(contentForPush);
                    } catch (error) {
                        console.error('Failed to parse tagmanagerlayer JSON:', error);
                    }
                }
            }
        });
    }

    private getFilterValue(element: HTMLInputElement): string | number {
        if (element.type === 'checkbox') {
            return element.checked ? 1 : 0;
        }
        return element.value;
    }

    public reloadModule(
        form: HTMLFormElement,
        hideFilter?: boolean,
        noScroll?: boolean,
        params?: FormData | Array<{name: string; value: FormDataEntryValue}>,
    ): Promise<void> {
        const moduleRoot = form.closest('.cms-module-root') as HTMLElement;
        if (!moduleRoot) {
            throw new Error('moduleRoot not found');
        }

        const cmsmoduleid = moduleRoot.getAttribute('data-module-id');
        if (!cmsmoduleid) {
            throw new Error('cmsmoduleid not found');
        }

        form = this.setFormData(moduleRoot, form);

        const formData = new FormData(form);

        if (!params) {
            params = formData;
        }

        let url = moduleRoot.getAttribute('data-modul-url') || '';

        document.body.classList.add('loading');

        return this.moduleCallbackLogitel(cmsmoduleid, params, url)
            .then((response) => response.text())
            .then((data) => {
                document.body.classList.remove('loading');

                let oldAjaxFootnotes = '';
                const footnotesForAjax = moduleRoot.querySelector('#footnotes_for_ajax');
                oldAjaxFootnotes = footnotesForAjax?.innerHTML || '';

                moduleRoot.innerHTML = data;

                const scriptDispatcher = new ScriptDispatcher();
                scriptDispatcher.dispatchScriptTag(data);
                scriptDispatcher.dispatchScriptFiles(data);

                // reinitialize all modules
                new TriggerReloadEvent(moduleRoot);

                const cmsContainer = notNull(moduleRoot.closest('.cms-module-root'));

                // offerboxer > oeffnen mehr details nach aenderung einer farbe oder einer option
                const moreDetailsTrigger = cmsContainer.querySelector('.more_details');
                if (moreDetailsTrigger) {
                    moreDetailsTrigger.classList.add('open');
                }

                if (hideFilter === undefined) {
                    // darf nur in phone passieren
                    // filter erneut oeffnen nach dem die tarifliste aktuallisiert wurde
                    if (document.body.clientWidth < 1024) {
                        const filter = cmsContainer.querySelector<HTMLElement>('.cms-tarif-list-filter,.cms-article-list-filter');
                        if (filter) {
                            notNull(document.querySelector('body')).classList.remove('no-scroll');
                            notNull(document.querySelector('html')).classList.remove('no-scroll');

                            filter.style.display = 'block';
                            new ScrollToElement(filter);

                            notNull(document.querySelector('body')).classList.add('no-scroll');
                            notNull(document.querySelector('html')).classList.add('no-scroll');
                            noScroll = true;
                        }
                    }
                }

                if (noScroll === undefined || noScroll === false) {
                    // zum start der liste scollen, zb beim sortieren (phone & desktop) oder beim filtern (desktop)
                    const liste = cmsContainer.querySelector<HTMLElement>('.cms-tarif-list-filter,.cms-article-list-filter');
                    if (liste) {
                        notNull(document.querySelector('body')).classList.remove('no-scroll');
                        notNull(document.querySelector('html')).classList.remove('no-scroll');
                        const scrollpos = liste.closest<HTMLElement>('.row');
                        if (scrollpos) {
                            new ScrollToElement(scrollpos);
                        }
                    }
                }

                if (oldAjaxFootnotes) {
                    // rechtstext im footer aktualisieren, nach aenderung der tarif/artikelliste
                    const newAjaxFootnotes = moduleRoot.querySelector('#footnotes_for_ajax')?.innerHTML || '';
                    const ajaxFootnotes = document.querySelector('#ajax_footnotes');
                    if (ajaxFootnotes) {
                        if (!ajaxFootnotes.innerHTML) {
                            ajaxFootnotes.innerHTML = newAjaxFootnotes;
                        } else {
                            ajaxFootnotes.innerHTML = ajaxFootnotes.innerHTML.replace(oldAjaxFootnotes, newAjaxFootnotes);
                        }
                    }
                }
            })
            .catch((error) => {
                document.body.classList.remove('loading');
                throw error;
            });
    }

    private setFormData(moduleRoot: HTMLElement, form: HTMLFormElement): HTMLFormElement {
        const provider = moduleRoot.getAttribute('data-provider');
        if (provider && !form.querySelector('input[name="defaultprovider"]')) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = 'defaultprovider';
            hiddenField.value = provider;
            form.appendChild(hiddenField);
        }

        const tarifids = moduleRoot.getAttribute('data-tarifids');
        if (tarifids && !form.querySelector('input[name="vtids"]')) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = 'vtids';
            hiddenField.value = tarifids;
            form.appendChild(hiddenField);
        }

        const isVvl = moduleRoot.getAttribute('data-isVvl');
        if (isVvl && !form.querySelector('input[name="isVvl"]')) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = 'isVvl';
            hiddenField.value = isVvl;
            form.appendChild(hiddenField);
        }

        const isLandingPage = moduleRoot.getAttribute('data-is-landing-page');
        if (isLandingPage && !form.querySelector('input[name="isLandingPage"]')) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = 'isLandingPage';
            hiddenField.value = isLandingPage;
            form.appendChild(hiddenField);
        }

        const anschlussart = moduleRoot.getAttribute('data-anschlussart');
        if (anschlussart && !form.querySelector('input[name="defaultanschlussart"]')) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = 'defaultanschlussart';
            hiddenField.value = anschlussart;
            form.appendChild(hiddenField);
        }

        const netz = moduleRoot.getAttribute('data-netz');
        if (netz && !form.querySelector('input[name="defaultnetz"]')) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = 'defaultnetz';
            hiddenField.value = netz;
            form.appendChild(hiddenField);
        }

        return form;
    }

    private moduleCallbackLogitel(cmsmoduleid: string, params: FormData | {name: string; value: FormDataEntryValue}[], loadUrl?: string): Promise<Response> {
        const url = loadUrl && loadUrl !== '' ? loadUrl : '/modulecms/api_modulecms_callback';

        if (params instanceof FormData) {
            params.append('cmsmoduleid', cmsmoduleid);

            const settings: RequestInit = {
                headers: {'X-Requested-With': 'XMLHttpRequest'},
                cache: 'default',
                credentials: 'same-origin',
                method: 'POST',
                body: params,
            };
            return fetch(url, settings);
        }

        const queryParams = new URLSearchParams();
        params.forEach((param) => queryParams.append(param.name, String(param.value)));
        queryParams.append('cmsmoduleid', cmsmoduleid);

        return fetch(url, {
            method: 'POST',
            headers: {'X-Requested-With': 'XMLHttpRequest'},
            body: queryParams,
        });
    }
}
